import { Group, Text } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CodeCell } from "~/components/cells/CodeCell.tsx"
import { DateCell } from "~/components/cells/DateCell.tsx"
import { ProductionSiteCell } from "~/components/cells/ProductionSiteCell.tsx"
import { FindCertificatesByOrganizationIdResponseDto } from "~/gen/api"
import { CertificateStatusBadge } from "~/pages/Brand/Certificates/components/CertificateStatusBadge.tsx"

const columnHelper =
  createColumnHelper<FindCertificatesByOrganizationIdResponseDto>()

export const useColumns = () => {
  const { t } = useTranslation("certificates")

  return useMemo<ColumnDef<FindCertificatesByOrganizationIdResponseDto, any>[]>(
    () => [
      columnHelper.accessor("type.name", {
        header: t("lists.columns.type"),
        cell: (info) => <Text fw={700}>{info.getValue()}</Text>,
      }),
      columnHelper.accessor("productionSite.name", {
        id: "company",
        header: t("lists.columns.productionSite"), // TODO: make country available for search
        cell: ({ row: { original: certificate } }) => {
          return (
            <ProductionSiteCell
              productionSite={certificate.productionSite ?? undefined}
              showAddress={false}
            />
          )
        },
      }),
      columnHelper.accessor(({ purchaseOrders }) => purchaseOrders.join(", "), {
        header: t("lists.columns.purchaseOrders"),
        cell: ({ row }) => {
          const pos = row.original.purchaseOrders
          if (!pos.length) return <Missing />
          return (
            <Group gap="xs" fz="xs">
              {pos.map((po) => (
                <CodeCell key={po} value={po} />
              ))}
            </Group>
          )
        },
      }),
      columnHelper.accessor("issueDate", {
        header: t("lists.columns.issueDate"),
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("expiryDate", {
        header: t("lists.columns.expiryDate"),
        cell: (info) => <DateCell date={info.getValue()} />,
      }),
      columnHelper.accessor("reference", {
        header: t("lists.columns.reference"),
        cell: (info) => <CodeCell value={info.getValue()} />,
      }),
      columnHelper.accessor("score", {
        header: t("lists.columns.score"),
      }),
      columnHelper.accessor("status", {
        header: t("lists.columns.status"),
        cell: (info) => {
          const value = info.getValue()
          return <CertificateStatusBadge status={value} />
        },
      }),
    ],
    [t]
  )
}
